// ═══════════════════════════════════════
// COMMON/MAIN — Point d'entrée common.min.js
// Chargé sur toutes les pages (SPA + statiques).
// ═══════════════════════════════════════

import { updateUserIcon, openUserModal } from './auth.js';
import { t } from '../i18n.js';
import { initImportButton } from '../import.js';

document.addEventListener('DOMContentLoaded', async () => {

    if (!localStorage.getItem('auth_token')) {
        await _tryRestoreSessionFromCookie();
    }

    updateUserIcon();

    const btnUser = document.getElementById('btn-user');
    if (btnUser) {
        btnUser.addEventListener('click', () => {
            const forceView = btnUser.dataset.openView || null;
            if (forceView) delete btnUser.dataset.openView;
            openUserModal(forceView);
        });
    }

    const btnHamburger  = document.getElementById('btn-hamburger');
    const hamburgerMenu = document.getElementById('hamburger-menu');
    if (btnHamburger && hamburgerMenu) {
        btnHamburger.addEventListener('click', (e) => {
            e.stopPropagation();
            const isOpen = hamburgerMenu.classList.toggle('open');
            btnHamburger.setAttribute('aria-expanded', isOpen);
        });
        document.addEventListener('click', () => {
            hamburgerMenu.classList.remove('open');
            btnHamburger.setAttribute('aria-expanded', 'false');
        });
    }

    const urlToken = new URLSearchParams(location.search).get('token');
    if (urlToken && location.pathname.includes('reset-password')) {
        // La modale est chargée en AJAX (api/modals.php) : il faut attendre
        // qu'elle soit réellement montée avant de chercher #reset-token dans
        // le DOM, sinon le champ n'existe pas encore et le token reste vide.
        await openUserModal('user-reset');
        const tokenInput = document.getElementById('reset-token');
        if (tokenInput) tokenInput.value = urlToken;
    }

    if (location.pathname.includes('login'))    openUserModal('user-login');
    if (location.pathname.includes('account'))  openUserModal('user-account');

    _initInstrumentFilters();
    _initFiltersToggle();
    _initLangSwitcher();
    initImportButton();
});

// ─────────────────────────────────────────
// Réhydratation silencieuse via cookie
// ─────────────────────────────────────────
async function _tryRestoreSessionFromCookie() {
    try {
        const res = await fetch('/api/users.php?action=cookie-login', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.success) {
            const { setSession } = await import('./auth.js');
            setSession(data.token, {
                id:       data.id,
                username: data.username,
                email:    data.email,
                role:     data.role,
            });
        }
    } catch (_) {}
}

// ─────────────────────────────────────────
// Sélecteur de langue
// ─────────────────────────────────────────
function _initLangSwitcher() {
    const supported   = window.__SUPPORTED_LANGS__ || ['fr', 'en'];
    const defaultLang = window.__DEFAULT_LANG__    || 'fr';

    document.querySelectorAll('.lang-switcher-btn:not([disabled])').forEach(btn => {
        btn.addEventListener('click', () => {
            const targetLang = btn.dataset.lang;
            const targetHref = btn.dataset.href;

            if (!targetLang || !targetHref || !supported.includes(targetLang)) return;

            // 1. Poser le cookie AVANT de naviguer
            const expires = new Date();
            expires.setFullYear(expires.getFullYear() + 1);
            document.cookie = 'scorou_geo_done=' + targetLang
                + '; path=/; expires=' + expires.toUTCString()
                + '; SameSite=Lax'
                + (location.protocol === 'https:' ? '; Secure' : '');

            // 2. Mettre à jour users.lang en DB si connecté
            const token = localStorage.getItem('auth_token');
            if (token) {
                fetch('/api/users.php?action=set-lang', {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + token,
                    },
                    body: JSON.stringify({ lang: targetLang }),
                    keepalive: true,
                }).catch(() => {});
            }

            // 3. Naviguer — le cookie est déjà dans document.cookie,
            //    il sera transmis dans la requête HTTP suivante
            location.href = targetHref;
        });
    });
}
// ─────────────────────────────────────────
// Filtres liste instruments
// ─────────────────────────────────────────
async function _initInstrumentFilters() {
    const list = document.getElementById('instrument-partition-list');
    if (!list) return;

    const allItems = [...list.querySelectorAll('.partition-item[data-title]')];
    if (!allItems.length) return;

    const searchEl  = document.getElementById('filter-search');
    const mineEl    = document.getElementById('filter-mine');
    const groupsDiv = document.getElementById('filter-groups');
    const groupList = document.getElementById('filter-groups-list');
    const countEl   = document.getElementById('filter-count');
    const emptyEl   = document.getElementById('instrument-empty');
    const paginEl   = document.getElementById('instrument-pagination');

    const PAGE_SIZE = 20;
    let currentPage   = 1;
    let filteredItems = allItems;

    function renderPage() {
        const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE);
        const page       = Math.max(1, Math.min(currentPage, totalPages || 1));
        const start      = (page - 1) * PAGE_SIZE;
        const end        = page * PAGE_SIZE;

        allItems.forEach(item => { item.style.display = 'none'; });
        filteredItems.slice(start, end).forEach(item => { item.style.display = ''; });

        if (countEl) {
            const n = filteredItems.length;
            countEl.textContent = n === 0 ? '' : t('list.count' + (n > 1 ? '.plural' : ''), { n });
        }

        if (emptyEl) {
            const hasFilter = (searchEl?.value ?? '').trim()
                || (mineEl?.checked ?? false)
                || document.querySelectorAll('.filter-group-chk:checked').length > 0;
            emptyEl.style.display = (filteredItems.length === 0 && hasFilter) ? '' : 'none';
        }

        if (paginEl) {
            if (totalPages <= 1) {
                paginEl.innerHTML = '';
            } else {
                const hasPrev   = page > 1;
                const hasNext   = page < totalPages;
                const dispStart = start + 1;
                const dispEnd   = Math.min(end, filteredItems.length);

                paginEl.innerHTML = `
                    <button class="pagin-btn" id="pagin-prev" ${hasPrev ? '' : 'disabled'}>${t('list.pagin_prev')}</button>
                    <span class="pagin-info">${t('list.pagin_info', { from: dispStart, to: dispEnd, total: filteredItems.length })}</span>
                    <button class="pagin-btn" id="pagin-next" ${hasNext ? '' : 'disabled'}>${t('list.pagin_next')}</button>
                `;
                if (hasPrev) {
                    paginEl.querySelector('#pagin-prev').addEventListener('click', () => {
                        currentPage--;
                        renderPage();
                        list.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    });
                }
                if (hasNext) {
                    paginEl.querySelector('#pagin-next').addEventListener('click', () => {
                        currentPage++;
                        renderPage();
                        list.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    });
                }
            }
        }
    }

    function applyFilters() {
        const search         = (searchEl?.value ?? '').toLowerCase().trim();
        const filterMine     = mineEl?.checked ?? false;
        const groupChks      = [...document.querySelectorAll('.filter-group-chk:checked')];
        const activeGroupIds = groupChks.map(el => parseInt(el.value));

        filteredItems = allItems.filter(item => {
            const title    = item.dataset.title ?? '';
            const isOwner  = item.dataset.owner === '1';
            const groupIds = JSON.parse(item.dataset.groups || '[]');

            if (search && !title.includes(search)) return false;
            if (filterMine && !isOwner) return false;
            if (activeGroupIds.length > 0 && !activeGroupIds.some(id => groupIds.includes(id))) return false;
            return true;
        });

        currentPage = 1;
        renderPage();
    }

    searchEl?.addEventListener('input', applyFilters);
    mineEl?.addEventListener('change', applyFilters);

    renderPage();

    const token = localStorage.getItem('auth_token');
    if (token && groupsDiv && groupList) {
        try {
            const res    = await fetch('/api/users.php?action=groups', {
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
            });
            const groups = await res.json();
            if (Array.isArray(groups) && groups.length > 0) {
                groupList.innerHTML = '';
                groups.forEach(g => {
                    const lbl = document.createElement('label');
                    lbl.className = 'filter-check';
                    lbl.innerHTML = `<input type="checkbox" class="filter-group-chk" value="${g.id}"><span>${g.name}</span>`;
                    lbl.querySelector('input').addEventListener('change', applyFilters);
                    groupList.appendChild(lbl);
                });
                groupsDiv.style.display = '';
            }
        } catch(e) { /* silencieux */ }
    }
}

// ─────────────────────────────────────────
// Overlay filtres mobile
// ─────────────────────────────────────────
function _initFiltersToggle() {
    const filters = document.getElementById('list-filters');
    if (!filters) return;
    if (window.innerWidth > 768) return;

    const btnOpen = document.createElement('button');
    btnOpen.id        = 'btn-filters-open';
    btnOpen.type      = 'button';
    btnOpen.className = 'btn-filters-open';
    btnOpen.innerHTML = `<span>${t('list.filters_btn')}</span>`;
    filters.parentNode.insertBefore(btnOpen, filters);

    const overlay = document.createElement('div');
    overlay.id        = 'filters-overlay';
    overlay.className = 'filters-overlay';
    document.body.appendChild(overlay);

    const btnApply = document.createElement('button');
    btnApply.type        = 'button';
    btnApply.className   = 'btn-filters-apply';
    btnApply.textContent = t('list.filters_apply');

    const btnClose = document.createElement('button');
    btnClose.type      = 'button';
    btnClose.className = 'btn-filters-close';
    btnClose.innerHTML = '✕';

    const overlayHeader = document.createElement('div');
    overlayHeader.className = 'filters-overlay-header';
    overlayHeader.appendChild(btnClose);
    overlayHeader.insertAdjacentHTML('afterbegin', `<span>${t('list.filters_title')}</span>`);

    overlay.appendChild(overlayHeader);
    overlay.appendChild(filters);
    overlay.appendChild(btnApply);

    function openOverlay() {
        overlay.classList.add('filters-overlay--open');
        document.body.style.overflow = 'hidden';
    }

    function closeOverlay() {
        overlay.classList.remove('filters-overlay--open');
        document.body.style.overflow = '';
    }

    btnOpen.addEventListener('click', openOverlay);
    btnClose.addEventListener('click', closeOverlay);
    btnApply.addEventListener('click', closeOverlay);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeOverlay(); });
}